import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Star, ShoppingCart, ArrowRight, Quote } from 'lucide-react';

const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8 flex justify-center">
              <div className="w-64 h-96 bg-gradient-to-br from-amber-600 to-amber-800 rounded-lg shadow-2xl flex items-center justify-center">
                <BookOpen className="w-32 h-32 text-white/20" />
              </div>
            </div>
            <h1 className="text-6xl font-bold mb-6">The Lost Code Mystery</h1>
            <p className="text-2xl mb-8 opacity-90">
              A gripping thriller about secrets, code, and the search for truth
            </p>
            <p className="text-xl mb-12 opacity-80 max-w-2xl mx-auto">
              When a brilliant cryptographer discovers a hidden message in an ancient manuscript, 
              she uncovers a conspiracy that could change history forever.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/buy" className="bg-amber-500 text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-amber-400 transition flex items-center">
                <ShoppingCart className="mr-2" /> Buy Now - $14.99
              </Link>
              <Link to="/chapters" className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-gray-900 transition">
                Read First Chapter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center text-gray-800">
            What Readers Are Saying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic">
                "A page-turner from start to finish. Victor Grösser has crafted a masterpiece of suspense and intrigue."
              </p>
              <div className="font-semibold">— Sarah Johnson, Book Review Magazine</div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic">
                "The Lost Code Mystery kept me up all night. The twists and turns are absolutely brilliant."
              </p>
              <div className="font-semibold">— Michael Chen, Thriller Enthusiast</div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic">
                "A perfect blend of cryptography, history, and modern-day thriller. Couldn't put it down!"
              </p>
              <div className="font-semibold">— Emma Williams, Mystery Reader</div>
            </div>
          </div>
        </div>
      </section>

      {/* About the Book */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center text-gray-800">
              About the Book
            </h2>
            <div className="prose prose-lg mx-auto">
              <p className="text-lg text-gray-700 mb-6">
                Dr. Elena Vasquez thought she was just decoding an old manuscript. But when she discovers 
                a hidden message that shouldn't exist, she's pulled into a web of secrets that spans centuries.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                As she races against time to uncover the truth, Elena must navigate dangerous conspiracies, 
                decode ancient puzzles, and confront a powerful organization that will stop at nothing to 
                keep its secrets buried.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                From the cryptic streets of Prague to the hidden archives of the Vatican, The Lost Code 
                Mystery takes readers on a breathtaking journey through history, cryptography, and the 
                lengths people will go to protect the truth.
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/buy" className="bg-amber-500 text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-amber-400 transition">
                  Buy Now
                </Link>
                <Link to="/chapters" className="border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-900 hover:text-white transition">
                  Read Sample
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Author Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8 text-gray-800">Meet the Author</h2>
            <div className="mb-8">
              <div className="w-48 h-48 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full mx-auto flex items-center justify-center">
                <span className="text-6xl text-white font-bold">VG</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold mb-4">Victor Grösser</h3>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Victor Grösser is a technology entrepreneur and writer with a passion for cryptography, 
              history, and mystery. The Lost Code Mystery is his debut novel, drawing on his extensive 
              experience in technology and his love for puzzles.
            </p>
            <Link to="/author" className="text-amber-600 font-semibold hover:text-amber-800 flex items-center justify-center">
              Learn More About the Author <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Uncover the Mystery?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of readers who have already discovered the secrets hidden in The Lost Code Mystery.
          </p>
          <Link to="/buy" className="bg-white text-amber-700 px-8 py-4 rounded-full font-semibold text-lg hover:bg-amber-50 transition inline-flex items-center">
            <ShoppingCart className="mr-2" /> Buy Now - $14.99
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;